import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Key } from 'qampo-ui-library/base';
import type { StaffResponse } from '../../api/generated';
import { DropdownList } from '../UI/base/DropdownList';
import { LoadError } from '../UI/LoadError';
import { LoadingPlaceholder } from '../UI/LoadingPlaceholder';
import type { CompetenceNameById } from './preferredStaffHelpers';
import { formatStaffOptionLabel, resolvePreferredStaff } from './preferredStaffHelpers';
import styles from './PatientDetailsCard.module.css';

// Sized like the sibling form fields, but allowed to grow when the chips wrap.
const TRIGGER_STYLES: React.CSSProperties = {
  height: 'auto',
  minHeight: '40px',
  padding: '6px 10px',
  border: '1px solid var(--color-border-control)',
  fontSize: 'var(--typography-size-paragraph-std)',
  fontWeight: 'var(--typography-weight-body)'
};

interface PreferredStaffPickerProps {
  preferredStaffIds: string[];
  allStaff: StaffResponse[];
  competenceNameById: CompetenceNameById;
  isStaffLoaded: boolean;
  hasStaffError: boolean;
  onRetryStaff: () => void;
  onChange: (preferredStaffIds: string[]) => void;
}

export const PreferredStaffPicker = ({
  preferredStaffIds,
  allStaff,
  competenceNameById,
  isStaffLoaded,
  hasStaffError,
  onRetryStaff,
  onChange
}: PreferredStaffPickerProps) => {
  const { t } = useTranslation();

  // Chips only. An id the roster does not cover renders no chip but stays in `preferredStaffIds`
  // and is saved back untouched, so a partly-loaded or wrong-location roster cannot delete it.
  // (The dropdown toggles against the full `value` array for the same reason.)
  const selectedStaff = useMemo(
    () => resolvePreferredStaff(preferredStaffIds, allStaff),
    [preferredStaffIds, allStaff]
  );

  const toggleStaff = useCallback(
    (member: StaffResponse) => {
      onChange(
        preferredStaffIds.includes(member.id)
          ? preferredStaffIds.filter((id) => id !== member.id)
          : [...preferredStaffIds, member.id]
      );
    },
    [preferredStaffIds, onChange]
  );

  const handleSelectionChange = useCallback(
    (next: Key[]): void => {
      onChange(next.map(String));
    },
    [onChange]
  );

  // Ignores the roster-ordered list the dropdown passes: chips render from the stored-order
  // resolution above, so the edit form lists people in the same places as the read-only view.
  const renderChips = (): React.ReactNode => {
    if (selectedStaff.length === 0) return t('preferred-staff-placeholder');
    return (
      <span className={styles.pickerChips}>
        {selectedStaff.map((member) => (
          <span key={member.id} className={styles.pill}>
            {member.fullName}
            {/* A span, unlike RoomNeedPicker's button: this chip sits inside the dropdown's
                trigger <button>, where a nested button is invalid HTML. stopPropagation keeps
                removal from also toggling the panel; keyboard removal goes through the option
                list's checkboxes. */}
            <span
              className={styles.pillRemove}
              aria-hidden="true"
              onClick={(event: React.MouseEvent<HTMLSpanElement>): void => {
                event.stopPropagation();
                toggleStaff(member);
              }}
            >
              &times;
            </span>
          </span>
        ))}
      </span>
    );
  };

  return (
    <>
      {/* The card's own label markup rather than the library's `label` prop, which takes a
          plain string and so cannot grey only the "(Optional)" part the sibling fields do. */}
      <span className={styles.fieldLabelSmall}>
        {t('add-patient-preferred-staff')}
        <span className={styles.optionalTag}>({t('patient-detail-optional')})</span>
      </span>
      {/* An unloaded roster would render as an empty picker, which says the patient has no
          preferred staff when the truth is that we cannot know yet. The stored ids are safe
          either way, but the user could otherwise only add, never see or remove what is
          already there. Nothing else in the form is withheld: the field is optional and the
          other fields do not depend on the roster. */}
      {hasStaffError && <LoadError message={t('preferred-staff-load-error')} onRetry={onRetryStaff} />}
      {!hasStaffError && !isStaffLoaded && <LoadingPlaceholder />}
      {!hasStaffError && isStaffLoaded && (
        <DropdownList<StaffResponse>
          options={allStaff}
          getKey={(member: StaffResponse): string => member.id}
          renderLabel={(member: StaffResponse): string =>
            formatStaffOptionLabel(member, competenceNameById)
          }
          selectionMode="multiple"
          value={preferredStaffIds}
          onChange={handleSelectionChange}
          placeholder={t('preferred-staff-placeholder')}
          renderTriggerValue={renderChips}
          styles={{ trigger: TRIGGER_STYLES }}
        />
      )}
    </>
  );
};
